import { zodResolver } from "@hookform/resolvers/zod";

import { Controller, useForm } from "react-hook-form";

import { useTranslation } from "react-i18next";

import { useNavigate } from "react-router-dom";

import FormTextField from "@/pages/Private/pages/Account/components/FormTextField";
import { useAcceptCourseProgressInvitationMutation } from "@/pages/Private/redux/course-progress/course-progress.api";
import FormContainer from "@/pages/Public/components/FormContainer";
import { ERROR_TYPE } from "@/utils";

import { FirstLoginType, FirstLoginSchema } from "../schema/firstLogin";

interface FirstNameLastNameFormProps {
	token: string | null;
	courseProgressId: string | null;
}

export default function FirstNameLastNameForm({
	courseProgressId,
	token,
}: FirstNameLastNameFormProps) {
	const { t } = useTranslation();
	const ts = (key: string) => t(`courses.progress.${key}`);
	const navigate = useNavigate();
	const [acceptInvitation, { error, isLoading }] = useAcceptCourseProgressInvitationMutation();
	const initialValues: FirstLoginType = {
		firstName: "",
		lastName: "",
	};
	const {
		register,
		control,
		handleSubmit,
		formState: { errors },
	} = useForm<FirstLoginType>({
		defaultValues: initialValues,
		resolver: zodResolver(FirstLoginSchema),
	});

	const onSubmit = async (values: FirstLoginType) => {
		try {
			const result = await acceptInvitation({
				user: {
					firstName: values?.firstName,
					lastName: values?.lastName,
				},
				accessToken: token ?? "",
				courseProgressId: courseProgressId ?? "",
			}).unwrap();

			if (result.courseProgressId) {
				navigate(`/app/scenarios/${result.courseProgressId}/progress`);
			}
		} catch (err) {
			console.error(err);
		}
	};

	return (
		<FormContainer
			description={ts("first-login.description")}
			formError={
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				(error as any)?.data?.message === ERROR_TYPE.USER_NOT_FOUND
					? ts(`first-login.${ERROR_TYPE.USER_NOT_FOUND}`)
					: error
			}
			handleSubmit={handleSubmit(onSubmit)}
			isLoading={isLoading}
			submitButtonText={ts("join-course")}
			title={ts("first-login.title")}
		>
			<FormTextField
				errorMessage={errors?.firstName?.message}
				formRegister={register("firstName")}
				label={t("basics.firstName")}
				placeHolder={t("basics.enterSomething", { something: t("basics.name") })}
			/>
			<Controller
				control={control}
				name="lastName"
				render={({ field }) => (
					<FormTextField
						{...field}
						errorMessage={errors?.lastName?.message}
						formRegister={register("lastName")}
						label={t("basics.lastName")}
						placeHolder={t("basics.enterSomething", { something: t("basics.lastName") })}
					/>
				)}
			/>
		</FormContainer>
	);
}
